import { cloneDeep, isNumber, isObject, isString, maxBy, minBy, random, tail } from 'lodash';
import tinycolor from 'tinycolor2';
import { v4 as uuid } from 'uuid';
import { defaultImageTransform } from '@meitu/saas-editor-core/es/layers/image-layer/constants/default-image-data';
import { getRotatePosition } from '@meitu/saas-editor-core/es/utils/collision-kit';
import { getImgInfoAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio, getGroupLayerProps } from '@meitu/saas-editor-core/es/utils/layer-kit';
import {
    transformLetterSpacingToStage,
    transformLowLeverTextDataToCss,
} from '@meitu/saas-editor-core/es/utils/transform-kit/to-client';
import { traverseTree, traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    IBrushLayerProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    IPoint,
    ITextLayerProps,
    IVectorLayerProps,
    LayerType,
    MaterialIdsType,
    TextAlignType,
    VectorType,
} from '@meitu/saas-editor-core/es/version-types';
import { measureTextLayerByEngine } from '@/utils/prepare-layer-fonts';
import { getJSONByUrl } from '@/utils/request';
import { getGradientColor } from './utils';

const Default_Chapter_Index_Values = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'];

export interface IPPTContentProps {
    page_type: string;
    data: {
        [key: string]: any;
    };
    json_url?: string;
    componentData?: any;
    component_id?: number;
    graph_components?: {
        [key: string]: any;
    }[];
}

interface ITemplateConfig {
    settingType: any;
    width: number;
    height: number;
    layers: ILayerProps[];
    extraInfo?: {
        label: {
            type: string;
            components: string[];
            sub_content_num: any;
        };
    };
}
interface ITemplateData {
    templateConf: ITemplateConfig[];
    [key: string]: any;
}

interface IApplyPPTTemplateProps {
    pptContents: IPPTContentProps[];
    templateData: ITemplateData;
    componentData?: ITemplateData;
    pageApplyCallback?: (data: ITemplateData) => void;
}

enum PageType {
    '封面' = '封面',
    '目录' = '目录',
    '章节' = '章节',
    '内容' = '内容',
    '致谢' = '致谢',
    '主题模板' = '主题模板',
}

interface IObj {
    [key: string]: any;
}
interface IThemeProps {
    colors: IObj;
    lightColors: IObj;
    darkColors: IObj;
    titleFont: ITextLayerProps | null | { fontFamily: string };
    subTitleFont: ITextLayerProps | null | { fontFamily: string };
    numberFont: ITextLayerProps | null | { fontFamily: string };
    numberFontId?: string[];
    themeType?: string;
    titleFontId?: string[];
    subTitleFontId?: string[];
}

export class Layer {
    static findLayerByType(layers: ILayerProps[], key: string) {
        let resultLayer;
        traverseTrees(layers, (layer: ILayerProps) => {
            const label = layer.extraInfo?.label;
            if (!label) {
                return false;
            }

            if (
                label.content_index !== null &&
                label.content_index !== undefined &&
                !!key.includes(`${label.type}:${label.content_index}`)
            ) {
                resultLayer = layer;
            } else if (
                !!key.includes(`${label.type}`) &&
                (label.content_index === null || label.content_index === undefined)
            ) {
                resultLayer = layer;
            }
        });

        return resultLayer;
    }

    static replaceLayerData(layer: ILayerProps, content: string | number, theme: IThemeProps) {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            return Layer.replaceTextLayer(layer, content, theme);
        } else if (layer.layerType === LayerType.IMAGE_LAYER) {
            return Layer.replaceImageLayer(layer, content, theme);
        } else if (layer.layerType === LayerType.VECTOR_LAYER) {
            return Layer.replaceVectorLayer(layer, theme);
        } else {
            return layer;
        }
    }

    static adjustTextSize(layer: ILayerProps, textExpend: string) {
        const { fontSize, text, letterSpacing } = layer.data as ITextLayerProps;

        const measure = (data: ITextLayerProps, fixedWidth: boolean) => {
            const measureData = transformLowLeverTextDataToCss(cloneDeep(data));
            return measureTextLayerByEngine({ ...layer, data: measureData }, fixedWidth);
        };

        if (textExpend === '纵向') {
            const measureData = {
                ...(layer.data as ITextLayerProps),
                text: text.substring(0, text.length - 1),
            };
            const { width, height } = measure(measureData, true);
            const oldWidth = width;

            layer.width = width;
            layer.height = height;

            if (text.endsWith('。')) {
                const { height: adjustHeight } = measure(measureData, true);

                if (adjustHeight < height) {
                    layer.width = width + fontSize + transformLetterSpacingToStage(letterSpacing, fontSize);
                    layer.height = adjustHeight;
                }
            }

            const { textAlign } = layer.data as ITextLayerProps;
            if (textAlign === TextAlignType.CENTER) {
                layer.left = layer.left - (width - oldWidth) / 2;
            } else if (textAlign === TextAlignType.RIGHT) {
                layer.left = layer.left + oldWidth - width;
            }
        } else if (textExpend === '横向') {
            let { width, height } = measure(layer.data as ITextLayerProps, false);

            width = width + fontSize / 2;
            const { textAlign } = layer.data as ITextLayerProps;
            if (textAlign === TextAlignType.CENTER) {
                layer.left = layer.left - (width - layer.width) / 2;
            } else if (textAlign === TextAlignType.RIGHT) {
                layer.left = layer.left + layer.width - width;
            }

            layer.width = width;
            layer.height = height;
        } else if (textExpend === '整体缩放') {
            const { width, height } = measure(layer.data as ITextLayerProps, false);

            const widthScale = layer.width / width;
            const heightScale = layer.height / height;

            const newFontSize = widthScale < heightScale ? fontSize * widthScale : fontSize * heightScale;
            (layer.data as ITextLayerProps).fontSize = newFontSize;
        }
    }

    static inheritColorAlpha(oldColor: string, newColor: string) {
        const alpha = tinycolor(oldColor).toRgb().a;
        const newColorData = tinycolor(newColor).toRgb();
        newColorData.a = alpha;
        return `#${tinycolor(newColorData).toHex8()}`;
    }

    static replaceTextLayer(layer: ILayerProps, content: string | number, theme: IThemeProps) {
        const data = layer.data as ITextLayerProps;
        const { extraInfo } = layer;
        if (content) {
            data.text = content.toString();
            data.textContents = undefined;
            layer.prompt = data.text;
        }

        if (extraInfo?.label) {
            const { type, fill, gradient_color, textExpend } = extraInfo.label;

            if (
                theme &&
                theme.titleFont &&
                (type === '列表标题' ||
                    type === '目录标题' ||
                    (type?.includes('标题') &&
                        !type?.includes('封面标题') &&
                        !type?.includes('封面副标题') &&
                        !type?.includes('章节标题')) ||
                    type?.includes('关键词'))
            ) {
                layer.materialIds = layer.materialIds || {};
                layer.materialIds[MaterialIdsType.FONT_FAMILY] = theme.titleFontId || [];

                data.fontFamily = theme.titleFont.fontFamily;
                if (data.textContents) {
                    data.textContents = data.textContents.map((item) => {
                        return {
                            ...item,
                            fontFamily: theme.titleFont?.fontFamily || item.fontFamily,
                        };
                    });
                }
            }

            if (
                theme &&
                theme.subTitleFont &&
                (type === '列表内容' ||
                    (type?.includes('内容') && !type?.includes('内容标题')) ||
                    type?.includes('节点时间'))
            ) {
                data.fontFamily = theme.subTitleFont.fontFamily;

                layer.materialIds = layer.materialIds || {};
                layer.materialIds[MaterialIdsType.FONT_FAMILY] = theme.subTitleFontId || [];

                if (data.textContents) {
                    data.textContents = data.textContents.map((item) => {
                        return {
                            ...item,
                            fontFamily: theme.subTitleFont?.fontFamily || item.fontFamily,
                        };
                    });
                }
            }

            if (type === '数字' && theme && theme.numberFont) {
                data.fontFamily = theme.numberFont.fontFamily;

                if (data.textContents) {
                    data.textContents = data.textContents.map((item) => {
                        return {
                            ...item,
                            fontFamily: theme.numberFont?.fontFamily || item.fontFamily,
                        };
                    });
                }
            }

            if (gradient_color && fill) {
                data.textGradient = getGradientColor(
                    theme.colors[fill] || theme.colors['主题色1'],
                    data.textGradient,
                    theme.themeType,
                );
            } else if (fill) {
                data.color = Layer.inheritColorAlpha(data.color, theme.colors[fill] || theme.colors['主题色1']);

                if (data.textContents) {
                    data.textContents = data.textContents.map((item) => {
                        return {
                            ...item,
                            color: Layer.inheritColorAlpha(item.color, theme.colors[fill] || theme.colors['主题色1']),
                        };
                    });
                }
            }

            // if (textExpend) {
            //     Layer.adjustTextSize(layer, textExpend);
            // } else {
            //     Layer.adjustTextSize(layer, '纵向');
            // }
        }

        return layer;
    }

    static replaceVectorLayer(layer: ILayerProps, theme: IThemeProps) {
        const data = layer.data as IVectorLayerProps;

        const { extraInfo } = layer;

        if (extraInfo?.label) {
            const { fill, gradient_color, stroke_color, stroke_gradient } = extraInfo.label;

            // FIXME 复杂图形我只是简单的把所有的颜色替换成一样的可能会有效果问题
            if (data.vectorType === VectorType.Graph && data.colors) {
                data.colors = data.colors.map((item) => {
                    return {
                        ...item,
                        color: fill
                            ? Layer.inheritColorAlpha(item.color, theme.colors[fill] || theme.colors['主题色1'])
                            : item.color,
                    };
                });
            } else {
                if (gradient_color && fill) {
                    data.fillGradient = getGradientColor(theme.colors[fill], data.fillGradient, theme.themeType);
                } else if (fill) {
                    data.fill = Layer.inheritColorAlpha(data.fill, theme.colors[fill] || theme.colors['主题色1']);
                }

                if (stroke_color && stroke_gradient) {
                    data.strokeGradient = getGradientColor(
                        theme.colors[stroke_color] || theme.colors['主题色1'],
                        data.strokeGradient,
                        theme.themeType,
                    );
                } else if (stroke_color) {
                    data.stroke = Layer.inheritColorAlpha(
                        data.stroke,
                        theme.colors[stroke_color] || theme.colors['主题色1'],
                    );
                }
            }
        }

        return layer;
    }

    static replaceBrushLayer(layer: ILayerProps, theme: IThemeProps) {
        const data = layer.data as IBrushLayerProps;

        const { extraInfo } = layer;

        if (extraInfo?.label) {
            const { fill, gradient_color, stroke_color, stroke_gradient } = extraInfo.label;

            if (data.fills && fill && gradient_color) {
                data.fills = data.fills.map((item) => {
                    return {
                        ...item,
                        color: getGradientColor(
                            theme.colors[stroke_color] || theme.colors['主题色1'],
                            '',
                            theme.themeType,
                        ),
                    };
                });
            } else if (data.fills && fill) {
                data.fills = data.fills.map((item) => {
                    return {
                        ...item,
                        color: fill
                            ? Layer.inheritColorAlpha(
                                  isString(item.color) ? item.color : '#ffffffff',
                                  theme.colors[fill] || theme.colors['主题色1'],
                              )
                            : item.color,
                    };
                });
            }

            if (data.stroke && stroke_color && stroke_gradient) {
                data.stroke = {
                    ...data.stroke,
                    color: getGradientColor(theme.colors[stroke_color] || theme.colors['主题色1'], '', theme.themeType),
                };
            } else if (data.stroke && stroke_color) {
                data.stroke = {
                    ...data.stroke,
                    color: Layer.inheritColorAlpha(
                        isString(data.stroke.color) ? data.stroke.color : '#ffffffff',
                        theme.colors[stroke_color] || theme.colors['主题色1'],
                    ),
                };
            }
        }

        return layer;
    }

    static adjustVectorLayerSize(layer: ILayerProps, parentLayer: ILayerProps) {}

    static replaceImageLayer(layer: ILayerProps, content: string | number, theme: IThemeProps) {
        (layer.data as IImageLayerProps).url = content as string;
        (layer as any).replaceImage = true;
        return layer;
    }
}

export class Page {
    static getOriginPage(templateData: any, pageType: string) {
        const { templateConf } = templateData;
        const page = templateConf.find((item: any) => {
            const label = item?.extraInfo?.label;
            if (!label) {
                return false;
            }

            return label.type === pageType;
        });

        if (!page) {
            return null;
        }

        return page;
    }
    static createPage(
        pptContent: IPPTContentProps,
        originPageData: ITemplateData,
        theme: IThemeProps,
        component?: ITemplateData,
    ) {
        const resultData = cloneDeep(originPageData);

        const themeType = resultData.extraInfo?.label?.theme;

        if (themeType) {
            themeType === '浅色调' ? (theme.colors = theme.darkColors) : (theme.colors = theme.lightColors);
            theme.themeType = themeType;
        } else {
            theme.colors = theme.lightColors;
        }
        if ((pptContent.page_type === '内容' || pptContent.page_type === '目录') && isObject(component)) {
            if (resultData.layers) {
                traverseTrees(resultData.layers, (layer: ILayerProps) => {
                    if (!layer.extraInfo) {
                        layer.extraInfo = {};
                    }

                    if (layer.extraInfo && layer.extraInfo.label) {
                        layer.extraInfo.label.canReplace = true;
                        layer.extraInfo.label.isOld = true;
                    } else {
                        layer.extraInfo.label = {
                            canReplace: true,
                            isOld: true,
                        };
                    }
                });
            }

            Page.applyComponent(pptContent, resultData, theme, component);
        } else {
            Page.replaceData(pptContent, resultData.layers, theme);
        }

        return resultData;
    }

    static replaceData(pptContent: IPPTContentProps, layers: ILayerProps[], theme: IThemeProps) {
        const { data: pptContentData } = pptContent;

        const keys = Object.keys(pptContentData);

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const content = pptContentData[key];
            const layer = Layer.findLayerByType(layers, key);

            if (layer) {
                const label = (layer as ILayerProps).extraInfo?.label;
                if ((layer as ILayerProps).layerType === LayerType.TEXT_LAYER && key === '章节序号' && label) {
                    label.originIndex = content;
                    Layer.replaceLayerData(
                        layer,
                        label.chapter_index_values
                            ? label.chapter_index_values[content - 1]
                            : Default_Chapter_Index_Values[content - 1]
                              ? Default_Chapter_Index_Values[content - 1]
                              : content,
                        theme,
                    );
                } else if ((layer as ILayerProps).layerType === LayerType.TEXT_LAYER) {
                    Layer.replaceLayerData(layer, content, theme);
                } else if ((layer as ILayerProps).layerType === LayerType.IMAGE_LAYER) {
                    Layer.replaceImageLayer(layer, content, theme);
                }
            }
        }

        let smartComponentLayer = null as ILayerProps | null;
        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.extraInfo?.label?.isSmartComponent) {
                smartComponentLayer = layer;
            }
        });

        if (smartComponentLayer && pptContent?.graph_components?.[0]) {
            const graphComponent = pptContent.graph_components[0];
            const graphDataKeys = Object.keys(graphComponent.graph_data);
            const smartComponentLayers = [] as ILayerProps[];
            traverseTree(smartComponentLayer, (layer: ILayerProps) => {
                smartComponentLayers.push(layer);
            });
            for (let i = 0; i < graphDataKeys.length; i++) {
                const key = graphDataKeys[i];
                const content = graphComponent.graph_data[key];
                const layer = Layer.findLayerByType(smartComponentLayers, key);
                if (layer) {
                    if ((layer as ILayerProps).layerType === LayerType.TEXT_LAYER) {
                        Layer.replaceLayerData(layer, content, theme);
                    } else if ((layer as ILayerProps).layerType === LayerType.IMAGE_LAYER) {
                        Layer.replaceImageLayer(layer, content, theme);
                    }
                }
            }
        }

        traverseTrees(layers, (layer: ILayerProps) => {
            const label = layer.extraInfo?.label;
            if (!label) {
                return;
            }

            // if (!label.type) {
            //     return;
            // }

            if (layer.layerType === LayerType.TEXT_LAYER) {
                Layer.replaceTextLayer(layer, '', theme);
            }

            if (layer.layerType === LayerType.VECTOR_LAYER) {
                Layer.replaceVectorLayer(layer, theme);
            }

            if (layer.layerType === LayerType.BRUSH_LAYER) {
                Layer.replaceBrushLayer(layer, theme);
            }
        });
    }

    static applyComponent(
        pptContent: IPPTContentProps,
        pageData: ITemplateData,
        theme: IThemeProps,
        component?: ITemplateData,
    ) {
        if (!component) {
            return null;
        }

        component.templateConf[0].layers.splice(0, 1);
        traverseTrees(component.templateConf[0].layers, (layer: ILayerProps) => {
            layer.id = uuid();
            if (layer.children && layer.children.length > 0) {
                for (let i = 0; i < layer.children.length; i++) {
                    if (layer.children[i]?.parentId) {
                        layer.children[i].parentId = layer.id;
                    }
                }
            }
        });

        pageData.layers = [...pageData.layers, ...component.templateConf[0].layers];

        Page.replaceData(pptContent, pageData.layers, theme);
    }
}

export class PPTTemplate {
    static generate(data: IApplyPPTTemplateProps) {
        const { pptContents, templateData, pageApplyCallback } = data;

        const templateConfigs = [] as any[];
        const theme = PPTTemplate.getTheme(templateData);

        //  删除母版图层
        PPTTemplate.removeMasterPageLayer(templateData);

        for (let i = 0; i < pptContents.length; i++) {
            const pptContent = pptContents[i];
            const originPageData = Page.getOriginPage(templateData, pptContent.page_type);

            if (!originPageData) {
                continue;
            }

            const component = PPTTemplate.getComponentFromPPTContent(pptContent);

            const templateConfig = Page.createPage(pptContent, originPageData, theme, component);
            templateConfig.componentId = pptContent.component_id;
            pageApplyCallback && pageApplyCallback(templateConfig);
            templateConfigs.push(templateConfig);
        }

        return templateConfigs;
    }
    static replaceBg(currentTemplate: ITemplateData, replaceTemplate: ITemplateData) {
        const contentPage = replaceTemplate.templateConf.find((item) => {
            const label = item.extraInfo?.label;
            if (!label) {
                return false;
            }

            if (label.type === '内容') {
                return true;
            }

            return false;
        });

        if (contentPage) {
            currentTemplate.layers[0] = contentPage.layers[0];
        }
    }
    static getComponentFromPPTContent(pptContent: IPPTContentProps) {
        if (pptContent.componentData) {
            return pptContent.componentData;
        }
        return undefined;
    }

    static getThemeV2(themeInfo: any) {
        const themeResult = {
            colors: {},
            titleFont: null,
            subTitleFont: null,
            darkColors: {},
            lightColors: {},
            numberFont: null,
            titleFontId: [],
            subTitleFontId: [],
            numberFontId: [],
        } as IThemeProps;

        themeInfo.darkColors.forEach((item: { [key: string]: string }, index: number) => {
            themeResult.darkColors[`主题色${index + 1}`] = item.mainThemeColor;
            themeResult.darkColors[`次中性色${index + 1}`] = item.subThemeColor;
            themeResult.darkColors[`中性色${index + 1}`] = item.thereThemeColor;
        });

        themeInfo.lightColors.forEach((item: { [key: string]: string }, index: number) => {
            themeResult.lightColors[`主题色${index + 1}`] = item.mainThemeColor;
            themeResult.lightColors[`次中性色${index + 1}`] = item.subThemeColor;
            themeResult.lightColors[`中性色${index + 1}`] = item.thereThemeColor;
        });

        themeResult.titleFont = {
            fontFamily: themeInfo.titleFontName,
        };
        themeResult.subTitleFont = {
            fontFamily: themeInfo.subTitleFontName,
        };
        themeResult.numberFont = {
            fontFamily: themeInfo.numberFontName,
        };

        themeResult.titleFontId = [themeInfo.titleFontId];
        themeResult.subTitleFontId = [themeInfo.subTitleFontId];
        themeResult.numberFontId = [themeInfo.numberFontId];

        return themeResult;
    }

    static getTheme(templateData: ITemplateData) {
        const { templateConf, extraInfo } = templateData;
        if (extraInfo && extraInfo.themeInfo) {
            return PPTTemplate.getThemeV2(extraInfo.themeInfo);
        }
        const masterPage = templateConf.find((item) => {
            const label = item.extraInfo?.label;
            if (!label) {
                return false;
            }

            if (label.type === PageType['主题模板']) {
                return true;
            }

            return false;
        });

        const themeResult = {
            colors: {},
            titleFont: null,
            subTitleFont: null,
            darkColors: {},
            lightColors: {},
            numberFont: null,
            titleFontId: [],
            subTitleFontId: [],
        } as IThemeProps;
        if (!masterPage) {
            return themeResult;
        }

        masterPage.layers.forEach((layer) => {
            if (layer.layerType === LayerType.GROUP_LAYER && layer.title === '深色模式') {
                layer.children.forEach((item) => {
                    const label = item.extraInfo?.label;
                    const color = (item.data as IVectorLayerProps).fill;
                    label && (themeResult.darkColors[label.fill] = color);
                });
            }

            if (layer.layerType === LayerType.GROUP_LAYER && layer.title === '浅色模式') {
                layer.children.forEach((item) => {
                    const label = item.extraInfo?.label;
                    const color = (item.data as IVectorLayerProps).fill;
                    label && (themeResult.lightColors[label.fill] = color);
                });
            }

            const label = layer.extraInfo?.label;
            if (!layer || !label) {
                return false;
            }

            if (layer.layerType === LayerType.TEXT_LAYER && label.type === '列表标题') {
                themeResult.titleFont = layer.data as ITextLayerProps;
                themeResult.titleFontId = layer.materialIds?.[MaterialIdsType.FONT_FAMILY];
            }

            if (layer.layerType === LayerType.TEXT_LAYER && label.type === '列表内容') {
                themeResult.subTitleFont = layer.data as ITextLayerProps;
                themeResult.subTitleFontId = layer.materialIds?.[MaterialIdsType.FONT_FAMILY];
            }

            if (layer.layerType === LayerType.TEXT_LAYER && label.type === '数字') {
                themeResult.numberFont = layer.data as ITextLayerProps;
                themeResult.numberFontId = layer.materialIds?.[MaterialIdsType.FONT_FAMILY];
            }
        });

        return themeResult as IThemeProps;
    }

    static removeMasterPageLayer(templateData: ITemplateData) {
        const { templateConf } = templateData;
        templateData.templateConf = templateConf.filter((item) => {
            const label = item.extraInfo?.label;
            if (!label) {
                return false;
            }

            if (label.type === PageType['主题模板']) {
                return false;
            }

            if (item.layers) {
                item.layers = item.layers.filter((layer: ILayerProps) => {
                    if (layer.title === '可删除') {
                        return false;
                    }
                    return true;
                });
            }
            return true;
        });
    }

    // 根据内容数据去获取组件
    static async getComponentsByPPTContentData(
        pptContentData: IPPTContentProps[],
        components: any[],
        transferTemplateToClient: (serverTemplate: any) => Promise<any>,
        directoryLayout: string,
        specifyComponentDatas?: any[],
    ) {
        const ids = [] as string[];
        const findSpecifyComponentDatas = async (contentNums: number, component_type: number) => {
            if (!specifyComponentDatas || specifyComponentDatas.length <= 0) {
                return null;
            }

            const targets = [] as any[];
            for (let i = 0; i < specifyComponentDatas.length; i++) {
                const specifyComponentData = specifyComponentDatas[i];
                if (
                    specifyComponentData &&
                    specifyComponentData.component_type === component_type &&
                    specifyComponentData.component_point === contentNums
                ) {
                    const templateConf = await transferTemplateToClient(JSON.parse(specifyComponentData.formula));
                    targets.push({ templateConf });
                }
            }

            return targets[random(0, targets.length - 1)];
        };
        for (let i = 0; i < pptContentData.length; i++) {
            const content = pptContentData[i];
            if (content.page_type === '内容') {
                const contentNums = Object.keys(content.data).filter((item) => item.includes('列表标题')).length;

                const data = await findSpecifyComponentDatas(contentNums, 2);
                if (data) {
                    content.componentData = data;
                    continue;
                }
                const contentComponents = components.filter((item) => {
                    return item.component_type === 2 && item.component_point === contentNums;
                });

                if (contentComponents.length > 0) {
                    const component = contentComponents[random(0, contentComponents.length - 1)];
                    content.componentData = component.json_url;
                    ids.push(component.json_url);
                }
            } else if (content.page_type === '目录') {
                const contentNums = Object.keys(content.data).filter((item) => item.includes('目录标题')).length;

                const data = await findSpecifyComponentDatas(contentNums, 1);
                if (data) {
                    content.componentData = data;
                    continue;
                }

                const directoryComponents = components.filter(
                    (item) =>
                        item.component_type === 1 &&
                        item.component_point === contentNums &&
                        item.layout === directoryLayout,
                );

                if (directoryComponents.length > 0) {
                    const component = directoryComponents[random(0, directoryComponents.length - 1)];

                    content.componentData = component.json_url;

                    ids.push(component.json_url);
                }
            }
        }

        if (ids.length > 0) {
            // const details = await fetchAIPPTMaterialDetailMulti(
            //     uniq(ids).join(',')
            // );

            const idMap = {} as any;
            for (let i = 0; i < ids.length; i++) {
                const formula = await getJSONByUrl(ids[i]);
                idMap[ids[i]] = formula;
            }

            for (let i = 0; i < pptContentData.length; i++) {
                const content = pptContentData[i];

                if (
                    content.componentData &&
                    (isNumber(content.componentData) || isString(content.componentData)) &&
                    idMap[content.componentData]
                ) {
                    const templateConf = await transferTemplateToClient(cloneDeep(idMap[content.componentData]));
                    content.componentData = { templateConf };
                }
            }
        }

        return pptContentData;
    }

    // 通过组件去获取 内容数据
    static async getPPTContentDataByComponents(
        pptContentData: IPPTContentProps[],
        transferTemplateToClient: (serverTemplate: any) => Promise<any>,
        specifyComponentDatas?: any[],
    ) {
        const contentData = [] as any;
        if (!specifyComponentDatas) {
            return pptContentData;
        }

        for (let i = 0; i < specifyComponentDatas.length; i++) {
            const specifyComponentData = specifyComponentDatas[i];
            const data = pptContentData.find((content) => {
                if (content.page_type === '内容') {
                    const contentNums = Object.keys(content.data).filter((item) => item.includes('列表标题')).length;
                    return (
                        specifyComponentData.component_point === contentNums &&
                        specifyComponentData.component_type === 2
                    );
                } else if (content.page_type === '目录') {
                    const contentNums = Object.keys(content.data).filter((item) => item.includes('目录标题')).length;
                    return (
                        specifyComponentData.component_point === contentNums &&
                        specifyComponentData.component_type === 1
                    );
                }
            });

            if (data) {
                const templateData = JSON.parse(specifyComponentData.formula);
                const templateConf = await transferTemplateToClient(cloneDeep(templateData));
                const result = cloneDeep(data);
                result.componentData = { templateConf };
                contentData.push(result);
            }
        }

        return contentData;
    }

    static async insertSmartComponentToComponent(component: any, smartComponent: any, contentData: any) {
        const targetComponent = smartComponent.find((item: any) => {
            return item.extraInfo?.componentPoint === contentData.graph_components[0].graph_component_point;
        });

        if (targetComponent) {
            traverseTrees(component[0].layers, (layer: ILayerProps) => {
                // 默认只有一个可替换图片
                if (layer.extraInfo?.label?.type?.includes('可替换图片')) {
                    let insertLayer = targetComponent.layers[1];
                    if (targetComponent.layers.length > 2) {
                        insertLayer = groupUpLayer(tail(targetComponent.layers));
                    }
                    Object.assign(layer, insertLayer);
                    layer.extraInfo.label = {
                        isSmartComponent: true,
                        moveable: true,
                    };
                }
            });
        }
    }
}

export const adjustImageLayerImage = async (layer: ILayerProps) => {
    if (layer.layerType !== LayerType.IMAGE_LAYER || !(layer as any).replaceImage) {
        return;
    }

    const { url } = layer.data as IImageLayerProps;
    const { width: imageWidth, height: imageHeight } = await getImgInfoAsync(url);
    const imgInfo = cloneDeep(layer.data) as IImageLayerProps;
    // 每次都要重置imageTransform
    imgInfo.naturalWidth = imageWidth;
    imgInfo.naturalHeight = imageHeight;
    imgInfo.url = url;
    imgInfo.cutoutMaskURL = '';
    imgInfo.effect = null;

    let imageTransform: IImageTransform = {
        ...cloneDeep(defaultImageTransform),
        widthRatio: 1,
        heightRatio: 1,
    };

    // 如果是自由模式还要计算下宽高比
    imageTransform = Object.assign(
        imageTransform,
        computerBgRatio({
            imgWidth: imageWidth,
            imgHeight: imageHeight,
            stageHeight: layer.height,
            stageWidth: layer.width,
        }),
    );
    imgInfo.imageTransform = imageTransform;

    layer.data = imgInfo;
    delete (layer as any).replaceImage;
};

export const adjustTextSize = (allTextLayers: ILayerProps[]) => {
    traverseTrees(allTextLayers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            const { extraInfo } = layer;

            if (extraInfo?.label) {
                const { textExpend } = extraInfo.label;
                if (textExpend) {
                    Layer.adjustTextSize(layer, textExpend);
                }
            }
        }

        if (layer.layerType !== LayerType.GROUP_LAYER) {
            const { extraInfo } = layer;
            if (!extraInfo) {
                layer.extraInfo = {};
                layer.extraInfo.label = { isOld: true };
            } else {
                extraInfo.label = {
                    ...extraInfo.label,
                    isOld: true,
                };
            }
        }
    });
};

export const findTemplateDirectoryLayout = (templateConf: any) => {
    let layout = '中';
    templateConf.find((item: any) => {
        const label = item.extraInfo?.label;
        if (label && label.type === '目录' && label.layout) {
            layout = label.layout;
        }
    });

    return layout;
};

export const findTemplatePageTheme = (templateConf: any, pageType: string) => {
    let theme = '';
    templateConf.find((item: any) => {
        const label = item.extraInfo?.label;
        if (label && label.theme && pageType === label.type) {
            theme = label.theme;
        }
    });

    return theme;
};
export const getNewLayers = (layers: ILayerProps[]) => {
    const newLayers = [] as ILayerProps[];
    layers = cloneDeep(layers);
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];

        const { extraInfo } = layer;

        if (!extraInfo || (extraInfo && !extraInfo.label)) {
            if (layer.layerType !== LayerType.GROUP_LAYER && layer.layerType !== LayerType.BACKGROUND_LAYER) {
                newLayers.push(layer);
            }

            continue;
        }

        const { type } = extraInfo.label;

        if (
            layer.layerType !== LayerType.GROUP_LAYER &&
            layer.layerType !== LayerType.BACKGROUND_LAYER &&
            !layer.extraInfo?.label?.isOld &&
            type !== '列表标题' &&
            type !== '列表内容' &&
            type !== '目录标题'
        ) {
            layer.parentId = '';
            newLayers.push({
                ...layer,
                id: uuid(),
            });
        } else if (layer.layerType === LayerType.GROUP_LAYER && layer.children && layer.children.length > 0) {
            getNewLayers(layer.children);
        }
    }

    return cloneDeep(newLayers);
};

export const randomMoveLayerPosition = (canMoveLayers: ILayerProps[]) => {
    if (canMoveLayers.length === 2) {
        const randomIndex = random(0, 1);
        const width = 852;
        const height = 736;
        const padding = 96;
        const gap = 24;
        const marginTop = 248;
        const leftCenterPoint = {
            x: width / 2 + padding,
            y: marginTop + height / 2,
        };

        const rightCenterPoint = {
            x: width + padding + gap + width / 2,
            y: marginTop + height / 2,
        };

        const leftLayer = canMoveLayers[randomIndex];
        const rightLayer = canMoveLayers[randomIndex === 1 ? 0 : 1];
        const originLeftLayerPositionOffset = {
            x: leftLayer.left + leftLayer.width / 2 - leftCenterPoint.x,
            y: leftLayer.top + leftLayer.height / 2 - leftCenterPoint.y,
        };

        const originRightLayerPositionOffset = {
            x: rightLayer.left + rightLayer.width / 2 - rightCenterPoint.x,
            y: rightLayer.top + rightLayer.height / 2 - rightCenterPoint.y,
        };

        traverseTree(canMoveLayers[randomIndex], (layer: ILayerProps) => {
            layer.left = layer.left - originLeftLayerPositionOffset.x;
            layer.top = layer.top - originLeftLayerPositionOffset.y;
        });

        traverseTree(canMoveLayers[randomIndex === 1 ? 0 : 1], (layer: ILayerProps) => {
            layer.left = layer.left - originRightLayerPositionOffset.x;
            layer.top = layer.top - originRightLayerPositionOffset.y;
        });
    }
};

export const groupUpLayer = (layers: ILayerProps[]) => {
    const positions = [] as IPoint[];
    const newGroupId = uuid();
    const children: ILayerProps[] = [];
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const { left, top, width, height, rotate } = layer;
        const position = getRotatePosition({
            left,
            top,
            width,
            height,
            rotate,
        });
        positions.push(...position);
        layer.parentId = newGroupId;
        children.push(layer);
    }

    const minX = minBy(positions, (pos) => pos.x) as IPoint;
    const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
    const minY = minBy(positions, (pos) => pos.y) as IPoint;
    const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
    const baseInfo = {
        left: minX.x,
        top: minY.y,
        width: maxX.x - minX.x,
        height: maxY.y - minY.y,
        rotate: 0,
        children,
    };
    const resultGroup = getGroupLayerProps({
        id: newGroupId,
        ...baseInfo,
        data: {
            isExtraGroup: false,
        },
    });

    return resultGroup;
};
